import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Spinner, Modal, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { usersAPI } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../utils/AuthContext';

function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'Developer',
    is_active: true
  });
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        full_name: user.full_name,
        email: user.email,
        role: user.role,
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        full_name: '',
        email: '',
        role: 'Developer',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      full_name: '',
      email: '',
      role: 'Developer',
      is_active: true
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name.trim() || !formData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    try {
      if (editingUser) {
        await usersAPI.updateUser(editingUser.id, formData);
        toast.success('User updated successfully!');
      } else {
        await usersAPI.createUser(formData);
        toast.success('User created successfully!');
      }
      handleCloseModal();
      fetchUsers();
    } catch (error) {
      toast.error(editingUser ? 'Failed to update user' : 'Failed to create user');
      console.error(error);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await usersAPI.deleteUser(userId);
      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
      console.error(error);
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      'Admin': 'danger',
      'Product': 'warning',
      'Developer': 'primary',
      'QA': 'info',
      'DevOps': 'success'
    };
    return <Badge bg={roleColors[role] || 'secondary'} style={{ borderRadius: '12px', padding: '4px 10px', fontSize: '12px', fontWeight: '500' }}>{role}</Badge>;
  };

  // Check if current user is admin
  if (currentUser?.role !== 'Admin') {
    return (
      <Container fluid style={{ padding: '2rem 3rem' }}>
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <Card.Body style={{ padding: '3rem', textAlign: 'center' }}>
            <h3 style={{ color: '#666' }}>Access Denied</h3>
            <p style={{ color: '#999' }}>You don't have permission to access this page.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: '2rem 3rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold mb-0" style={{ fontSize: '28px', color: '#1a1a1a' }}>Users</h2>
        <Button
          variant="primary"
          onClick={() => handleShowModal()}
          style={{
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: '600',
            fontSize: '14px',
            border: 'none'
          }}
        >
          + Add User
        </Button>
      </div>

      {/* Users Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
        <Card.Body style={{ padding: '1.5rem' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <tr>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>ID</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Name</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Email</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Role</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Status</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Created</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ fontSize: '14px', padding: '12px 8px' }}>
                          <strong style={{ color: '#0066cc' }}>#{user.id}</strong>
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#333' }}>
                          <div className="d-flex align-items-center">
                            {user.avatar_url && (
                              <img
                                src={user.avatar_url}
                                alt={user.full_name}
                                className="rounded-circle me-2"
                                width="32"
                                height="32"
                                style={{ objectFit: 'cover' }}
                              />
                            )}
                            <span className="fw-semibold">{user.full_name}</span>
                          </div>
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{user.email}</td>
                        <td style={{ padding: '12px 8px' }}>{getRoleBadge(user.role)}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <Badge
                            bg={user.is_active ? 'success' : 'secondary'}
                            style={{ borderRadius: '12px', padding: '4px 10px', fontSize: '12px', fontWeight: '500' }}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#999' }}>
                          {format(new Date(user.created_at), 'MMM dd, yyyy')}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowModal(user)}
                              style={{ borderRadius: '6px', fontSize: '12px', padding: '4px 12px' }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(user.id)}
                              disabled={user.id === currentUser.id}
                              style={{ borderRadius: '6px', fontSize: '12px', padding: '4px 12px' }}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted" style={{ padding: '24px' }}>
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit User Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e0e0e0' }}>
          <Modal.Title style={{ fontSize: '20px', fontWeight: '600' }}>
            {editingUser ? 'Edit User' : 'Add User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                Full Name <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Enter full name"
                required
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                Email <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email"
                required
                disabled={editingUser !== null}
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              />
              {editingUser && (
                <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                  Email cannot be changed
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                Role <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                style={{
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <option value="Admin">Admin</option>
                <option value="Product">Product</option>
                <option value="Developer">Developer</option>
                <option value="QA">QA</option>
                <option value="DevOps">DevOps</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                label="Active"
                style={{ fontSize: '14px', color: '#333' }}
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button
                variant="primary"
                type="submit"
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  fontSize: '14px',
                  border: 'none',
                  flex: 1
                }}
              >
                {editingUser ? 'Update User' : 'Add User'}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleCloseModal}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '500',
                  fontSize: '14px',
                  flex: 1
                }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default UsersList;
